import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { useWorkerProfilePicture } from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { WorkerAvatar } from "@src/appV2/redesign/components/WorkerAvatar";
import { CbhIcon, Text, useModalState } from "@src/appV2/redesign/system/ui-components";
import { useDeleteCommentReaction } from "@src/appV2/redesign/Workplace/Review/api/useDeleteCommentReaction";
import { usePostCommentReaction } from "@src/appV2/redesign/Workplace/Review/api/usePostCommentReaction";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { formatDistanceToNow, parseISO } from "date-fns";

import { Reaction } from "../types";
import { ConfirmRemoveCommentBottomSheet } from "./ConfirmRemoveCommentBottomSheet";

interface InlineReplyCardProps {
  reply: WorkplaceCommentType;
  workplaceId: string;
  viewerIsAuthor: boolean;
  onLikeChanged: (comment: WorkplaceCommentType) => void;
  onRemoved: (commentId: string) => void;
}

export function InlineReplyCard(props: InlineReplyCardProps) {
  const { reply, workplaceId, viewerIsAuthor, onLikeChanged, onRemoved } = props;
  const { workerInfo, text, createdAt, workerReaction, aggregates } = reply.attributes;

  const isLiked = workerReaction === Reaction.LIKE;
  const removeModalState = useModalState();

  const { data: profilePicture, isInitialLoading: isProfilePictureLoading } =
    useWorkerProfilePicture(String(workerInfo.id), {
      enabled: isDefined(workerInfo.id),
    });

  const { mutate: postCommentReaction } = usePostCommentReaction(
    { workplaceId, commentId: reply.id },
    {
      onSuccess: (data) => {
        onLikeChanged(data.data);
      },
    }
  );

  const { mutate: deleteCommentReaction } = useDeleteCommentReaction(
    { workplaceId, commentId: reply.id },
    {
      onSuccess: (data) => {
        onLikeChanged(data.data);
      },
    }
  );

  function handleLikeToggle() {
    if (isLiked) {
      deleteCommentReaction();
    } else {
      postCommentReaction({ reaction: Reaction.LIKE });
    }
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack direction="row" spacing={3} alignItems="flex-start">
          <WorkerAvatar
            profilePictureUrl={profilePicture?.distribution_url}
            isLoading={isProfilePictureLoading}
            size="small"
          />

          <Box
            sx={(theme) => ({
              backgroundColor: theme.palette.background.tertiary,
              borderRadius: 3,
              flex: 1,
              px: 5,
            })}
          >
            <Stack direction="row" justifyContent="space-between" sx={{ marginBottom: 1 }}>
              <Text semibold variant="body2">
                {workerInfo.name}
              </Text>
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {formatDistanceToNow(parseISO(createdAt))} ago
              </Text>
            </Stack>
            <Text variant="body2">{text}</Text>
          </Box>
        </Stack>

        <Stack direction="row" spacing={3} alignItems="center" sx={{ paddingLeft: 11 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ cursor: "pointer" }}
            onClick={(event) => {
              event.stopPropagation();
              handleLikeToggle();
            }}
          >
            <CbhIcon
              type={isLiked ? "like-filled" : "like-outlined"}
              size="small"
              color={(theme) => theme.palette.text.secondary}
            />
            {aggregates.totalLikes > 0 && (
              <Text variant="caption" color={(theme) => theme.palette.text.secondary}>
                {aggregates.totalLikes}
              </Text>
            )}
          </Stack>

          {viewerIsAuthor && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ cursor: "pointer" }}
              onClick={(event) => {
                event.stopPropagation();
                removeModalState.openModal();
              }}
            >
              <CbhIcon type="trash" size="small" color={(theme) => theme.palette.error.main} />
            </Stack>
          )}
        </Stack>
      </Stack>

      {viewerIsAuthor && (
        <ConfirmRemoveCommentBottomSheet
          modalState={removeModalState}
          commentId={reply.id}
          workplaceId={workplaceId}
          onRemoved={onRemoved}
        />
      )}
    </>
  );
}
